import {
  acknowledgeOperations,
  claimPendingOperations,
  getPendingMedia,
  moveOperationToConflict,
  rebasePendingRecord,
  resetMediaUpload,
  updateMediaUpload,
} from './offline'
import {
  blobToBase64,
  MEDIA_CHUNK_SIZE,
  reconstructVerifiedMedia,
  sha256Base64,
  sha256Hex,
  type MediaManifest,
} from './media-chunks'
import type { ChecklistTemplate, RevisionOperation, VisitSnapshot } from './types'

interface ApiErrorBody {
  error?: string
  code?: string
  conflicts?: Array<{ operationId: string; serverRecord: unknown; serverRevision: number }>
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/${path}`, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...init?.headers },
    ...init,
  })
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorBody
  if (!response.ok) {
    const error = new Error(body.error || `Request failed (${response.status})`)
    Object.assign(error, { status: response.status, body })
    throw error
  }
  return body
}

export async function unlockVisit(slug: string, contributor: string) {
  return request<{ visitId: string; expiresAt: string }>('unlock', {
    method: 'POST',
    body: JSON.stringify({ slug, contributor }),
  })
}

export async function loadRemoteVisit(slug: string) {
  return request<{ visit: VisitSnapshot }>(`visit?slug=${encodeURIComponent(slug)}`)
}

export async function syncOperationsDirect(operations: RevisionOperation[]) {
  const result = await request<{
    accepted: Array<{ operationId: string; revision: number }>
    conflicts: Array<{ operationId: string; serverRecord: unknown; serverRevision: number }>
  }>('sync', {
    method: 'POST',
    body: JSON.stringify({ operations }),
  })
  return {
    ...result,
    accepted: result.accepted.flatMap((accepted) => {
      const operation = operations.find((item) => item.id === accepted.operationId)
      return operation
        ? [{
            ...accepted,
            recordType: operation.recordType,
            recordId: operation.recordId,
          }]
        : []
    }),
  }
}

export async function syncPendingOperations(
  visitId: string,
  onAccepted?: (records: Array<{
    recordType: RevisionOperation['recordType']
    recordId: string
    revision: number
  }>) => Promise<void>,
) {
  const acceptedRecords: Array<{
    recordType: RevisionOperation['recordType']
    recordId: string
    revision: number
  }> = []
  let conflictCount = 0

  while (true) {
    const operations = await claimPendingOperations(visitId, 100)
    if (!operations.length) break
    const result = await request<{
      accepted: Array<{ operationId: string; revision: number }>
      conflicts: Array<{ operationId: string; serverRecord: unknown; serverRevision: number }>
    }>('sync', {
      method: 'POST',
      body: JSON.stringify({ operations }),
    })

    const acceptedBatch: typeof acceptedRecords = []
    for (const accepted of result.accepted) {
      const operation = operations.find((item) => item.id === accepted.operationId)
      if (!operation) continue
      acceptedBatch.push({
        recordType: operation.recordType,
        recordId: operation.recordId,
        revision: accepted.revision,
      })
    }
    if (acceptedBatch.length) {
      await onAccepted?.(acceptedBatch)
      acceptedRecords.push(...acceptedBatch)
    }
    await acknowledgeOperations(result.accepted.map((item) => item.operationId))
    for (const accepted of result.accepted) {
      const operation = operations.find((item) => item.id === accepted.operationId)
      if (!operation) continue
      await rebasePendingRecord(
        visitId,
        operation.recordType,
        operation.recordId,
        accepted.revision,
      )
    }
    for (const conflict of result.conflicts) {
      const operation = operations.find((item) => item.id === conflict.operationId)
      if (operation) {
        await moveOperationToConflict({
          operation,
          serverRecord: conflict.serverRecord,
          serverRevision: conflict.serverRevision,
        })
        conflictCount += 1
      }
    }
    if (!result.accepted.length && !result.conflicts.length) {
      throw new Error('The server did not acknowledge the queued operations.')
    }
    if (result.conflicts.length) break
  }
  return { synced: acceptedRecords.length, conflicts: conflictCount, acceptedRecords }
}

async function uploadOneMedia(
  visit: VisitSnapshot,
  item: Awaited<ReturnType<typeof getPendingMedia>>[number],
  canRestart = true,
) {
  let uploadId = item.uploadId
  let objectKey = item.objectKey
  let completedParts = item.completedParts ?? []

  if (!uploadId || !objectKey) {
    const evidence = visit.evidence.find((entry) => entry.id === item.id)
    const init = await request<{ uploadId?: string; objectKey: string; completed?: boolean }>('media', {
      method: 'POST',
      body: JSON.stringify({
        action: 'init',
        visitId: visit.id,
        evidenceId: item.id,
        fileName: evidence?.fileName ?? item.id,
        contentType: item.blob.type || evidence?.mimeType || 'application/octet-stream',
        size: item.blob.size,
      }),
    })
    if (init.completed) {
      await updateMediaUpload(item.id, { objectKey: init.objectKey, uploaded: true })
      return { id: item.id, objectKey: init.objectKey }
    }
    if (!init.uploadId) throw new Error('Media upload did not return an upload ID')
    uploadId = init.uploadId
    objectKey = init.objectKey
    await updateMediaUpload(item.id, { uploadId, objectKey, completedParts })
  }

  const totalParts = Math.max(1, Math.ceil(item.blob.size / MEDIA_CHUNK_SIZE))
  for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
    if (completedParts.some((part) => part.PartNumber === partNumber)) continue
    const start = (partNumber - 1) * MEDIA_CHUNK_SIZE
    const chunk = item.blob.slice(start, Math.min(item.blob.size, start + MEDIA_CHUNK_SIZE))
    const ChecksumSHA256 = await sha256Base64(chunk)
    try {
      await request('media', {
        method: 'POST',
        body: JSON.stringify({
          action: 'upload-chunk',
          evidenceId: item.id,
          objectKey,
          uploadId,
          partNumber,
          checksum: ChecksumSHA256,
          data: await blobToBase64(chunk),
        }),
      })
    } catch (error) {
      if (canRestart && (error as { status?: number }).status === 409) {
        await resetMediaUpload(item.id)
        return uploadOneMedia(visit, {
          ...item,
          uploadId: undefined,
          objectKey: undefined,
          completedParts: undefined,
        }, false)
      }
      throw error
    }
    completedParts = [...completedParts, {
      ETag: ChecksumSHA256,
      PartNumber: partNumber,
      ChecksumSHA256,
    }]
    await updateMediaUpload(item.id, { completedParts })
  }

  try {
    await request('media', {
      method: 'POST',
      body: JSON.stringify({
        action: 'complete',
        visitId: visit.id,
        evidenceId: item.id,
        objectKey,
        uploadId,
        parts: completedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        checksum: await sha256Hex(item.blob),
      }),
    })
  } catch (error) {
    if (canRestart && (error as { status?: number }).status === 409) {
      await resetMediaUpload(item.id)
      return uploadOneMedia(visit, {
        ...item,
        uploadId: undefined,
        objectKey: undefined,
        completedParts: undefined,
      }, false)
    }
    throw error
  }
  await updateMediaUpload(item.id, { uploaded: true })
  return { id: item.id, objectKey }
}

export async function uploadMediaDirect(input: {
  visitId: string
  evidenceId: string
  fileName: string
  blob: Blob
}) {
  const init = await request<{ uploadId?: string; objectKey: string; completed?: boolean }>('media', {
    method: 'POST',
    body: JSON.stringify({
      action: 'init',
      visitId: input.visitId,
      evidenceId: input.evidenceId,
      fileName: input.fileName,
      contentType: input.blob.type || 'application/octet-stream',
      size: input.blob.size,
    }),
  })
  if (init.completed) return { objectKey: init.objectKey }
  if (!init.uploadId) throw new Error('Media upload did not return an upload ID')

  const totalParts = Math.max(1, Math.ceil(input.blob.size / MEDIA_CHUNK_SIZE))
  const parts: Array<{ ETag: string; PartNumber: number; ChecksumSHA256: string }> = []
  for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
    const start = (partNumber - 1) * MEDIA_CHUNK_SIZE
    const chunk = input.blob.slice(start, Math.min(input.blob.size, start + MEDIA_CHUNK_SIZE))
    const ChecksumSHA256 = await sha256Base64(chunk)
    await request('media', {
      method: 'POST',
      body: JSON.stringify({
        action: 'upload-chunk',
        evidenceId: input.evidenceId,
        objectKey: init.objectKey,
        uploadId: init.uploadId,
        partNumber,
        checksum: ChecksumSHA256,
        data: await blobToBase64(chunk),
      }),
    })
    parts.push({ ETag: ChecksumSHA256, PartNumber: partNumber, ChecksumSHA256 })
  }

  await request('media', {
    method: 'POST',
    body: JSON.stringify({
      action: 'complete',
      visitId: input.visitId,
      evidenceId: input.evidenceId,
      objectKey: init.objectKey,
      uploadId: init.uploadId,
      parts,
      checksum: await sha256Hex(input.blob),
    }),
  })
  return { objectKey: init.objectKey }
}

export async function syncPendingMedia(visit: VisitSnapshot) {
  const activeIds = new Set(visit.evidence.filter((item) => !item.deletedAt).map((item) => item.id))
  const pending = (await getPendingMedia(visit.id)).filter((item) => activeIds.has(item.id))
  const uploaded: Array<{ id: string; objectKey: string }> = []
  for (const item of pending) {
    const result = await uploadOneMedia(visit, item)
    if (result.objectKey) uploaded.push({ id: result.id, objectKey: result.objectKey })
  }
  return uploaded
}

export async function getRemoteMediaBlob(evidenceId: string) {
  const manifest = await request<MediaManifest>('media', {
    method: 'POST',
    body: JSON.stringify({ action: 'manifest', evidenceId }),
  })
  const chunks: Array<{ number: number; checksum: string; data: string }> = []
  for (const expected of manifest.chunks.sort((a, b) => a.number - b.number)) {
    const chunk = await request<{ partNumber: number; checksum: string; data: string }>('media', {
      method: 'POST',
      body: JSON.stringify({ action: 'download-chunk', evidenceId, partNumber: expected.number }),
    })
    if (chunk.partNumber !== expected.number) {
      throw new Error(`Evidence chunk ${expected.number} failed integrity validation.`)
    }
    chunks.push({ number: chunk.partNumber, checksum: chunk.checksum, data: chunk.data })
  }
  return reconstructVerifiedMedia(manifest, chunks)
}

export async function createVisitOnServer(input: {
  adminPassword: string
  client: string
  siteName: string
  address: string
  templateId: string
  visitDate: string
}) {
  return request<{ slug: string; url: string }>('admin-visits', {
    method: 'POST',
    headers: { 'x-admin-password': input.adminPassword },
    body: JSON.stringify(input),
  })
}

export async function revokeVisitSessions(input: {
  adminPassword: string
  slug: string
}) {
  return request<{ revoked: boolean }>('admin-visits', {
    method: 'PATCH',
    headers: { 'x-admin-password': input.adminPassword },
    body: JSON.stringify(input),
  })
}

export async function listTemplates(adminPassword: string) {
  return request<{ templates: ChecklistTemplate[] }>('admin-templates', {
    headers: { 'x-admin-password': adminPassword },
  })
}

export async function saveTemplate(adminPassword: string, template: ChecklistTemplate) {
  return request<{ template: ChecklistTemplate }>('admin-templates', {
    method: 'POST',
    headers: { 'x-admin-password': adminPassword },
    body: JSON.stringify({ template }),
  })
}

